import PathEngine from './pathfinding/PathEngine'


export default class ClientController {

    constructor(world, args) {
        this.world = world

        this.interface = world.interface
        this.network = world.network
        this.crumbs = world.crumbs

        this.getString = world.getString

        // Assign user attributes
        let { user, ...attributes } = args
        Object.assign(this, attributes)

        this.stamps = Array.isArray(this.stamps)
            ? [...new Set(this.stamps.map(stamp => parseInt(stamp)).filter(Number.isFinite))]
            : []

        this.id = user.id
        this.joinTime = user.joinTime

        // Initialize arrays if not present
        if (!this.buddies) {
            this.buddies = []
        }
        if (!this.ignores) {
            this.ignores = []
        }
        if (!this.postcards) {
            this.postcards = []
        }
        if (!this.inventory) {
            this.inventory = []
        }
        // Initialize quest arrays for Quest Log
        if (!this.activeQuests) {
            this.activeQuests = []
        }
        if (!this.completedQuests) {
            this.completedQuests = []
        }

        this.iglooOpen = false

        this.lastRoom
        this.activeSeat
        this.lastSledId

        // Item inventory
        this.slots = ['color', 'head', 'face', 'neck', 'body', 'hand', 'feet', 'flag', 'photo', 'award']
        this.inventory = this.initInventory()

        this.sortPostcards()

        // Reference to ClientPenguin object
        this.penguin

        // If expecting emote key combo
        this.emoteKeyPressed = false
        this.emoteQueue = [] // Queue for multi-emote
        this.emoteQueueTimer = null
        this.resetQueueDelay = 2000 // Time to keep the queue alive for adding more emotes

        // Input
        this.keys = this.crumbs.quick_keys.keys
        this.emotes = this.crumbs.quick_keys.emotes

        this.keyActions = {
            'send_frame': (id) => this.sendFrame(id),
            'send_wave': () => this.sendFrame(25, false),
            'send_sit': () => this.sendSit(this.input.mousePointer),

            'show_crosshair': () => this.showCrosshair(),

            'emote_key': () => this.emoteKeyPressed = true,
            'send_emote': (id) => this.sendEmote(id),
            'send_safe': (id) => this.sendSafe(id),
            'send_joke': () => this.sendJoke()
        }

        this.lockRotation = false

        // Last join position (pre-randomization) for block variant selection in maze rooms
        this.lastJoinPos = null

        this.input.on('pointermove', (pointer) => this.onPointerMove(pointer))

        this.input.keyboard.on('keydown', (event) => this.onKeyDown(event))
    }

    get isTweening() {
        return this.penguin.isTweening
    }

    get visible() {
        return this.penguin.visible
    }

    get input() {
        return this.interface.main.input
    }

    get isModerator() {
        return this.rank > 1
    }

    get isTourGuide() {
        return this.inventory && this.inventory.head && this.inventory.head.includes(428)
    }

    get isSecretAgent() {
        return this.inventory && this.inventory.award && this.inventory.award.includes(800)
    }

    get daysOld() {
        const oneDay = 1000 * 60 * 60 * 24
        const timeDiff = Date.now() - Date.parse(this.joinTime)

        return Math.round(timeDiff / oneDay)
    }

    get mailCount() {
        return this.postcards ? this.postcards.length : 0
    }

    get unreadMailCount() {
        return this.postcards ? this.postcards.filter(postcard => !postcard.hasRead).length : 0
    }

    initInventory() {
         // Generates object from slots in format: { color: [], head: [], ... }
        let inventory = Object.fromEntries(this.slots.map(slot => [slot, []]))

        // Ensure inventory is an array before processing
        if (!this.inventory || !Array.isArray(this.inventory)) {
            this.inventory = []
        }

        // Assigns inventory list to slots
        for (let item of this.inventory) {
            item = parseInt(item)

            if (!(item in this.crumbs.items)) {
                continue
            }

            let type = this.crumbs.items[item].type
            let slot = this.slots[type - 1]

            inventory[slot].push(item)
        }

        return inventory
    }

    onPointerMove(pointer) {
        if (this.interface.main.crosshair?.visible) {
            this.interface.main.onCrosshairMove(pointer)
        }

        if (!this.visible || this.isTweening || this.lockRotation) {
            return
        }

        this.penguin.rotate(pointer.x, pointer.y)
    }

    onPointerUp(pointer, target) {
        if (pointer.button != 0 || !this.visible || this.activeSeat) {
            return
        }

        // Block movement when clicking a button
        if (target[0] && target[0].isButton) {
            return
        }

        this.sendMove(pointer.x, pointer.y)
    }

    onKeyDown(event) {
        let key = event.key.toLowerCase()

        console.log('[MultiEmote] Key pressed:', key, 'emoteKeyPressed:', this.emoteKeyPressed, 'queue:', this.emoteQueue)

        if (this.emoteKeyPressed) {
            this.processEmote(key)
        } else {
            this.processKey(key)
        }
    }

    processEmote(key) {
        // Special case: pressing E again sends immediately (if we were buffering) 
        // But with instant mode, it just closes the chain
        if (key === 'e') {
            this.emoteKeyPressed = false
            if (this.emoteQueueTimer) {
                clearTimeout(this.emoteQueueTimer)
                this.emoteQueueTimer = null
                this.emoteQueue = [] 
            }
            return
        }
        
        // Check if this is an emote key
        if (key in this.emotes) {
            const emoteId = this.emotes[key]
            
            // Add to queue if not already present
            if (!this.emoteQueue.includes(emoteId)) {
                this.emoteQueue.push(emoteId)
                
                // INSTANTLY send the current queue state
                if (this.emoteQueue.length === 1) {
                    this.sendEmote(this.emoteQueue[0])
                } else {
                    this.sendMultiEmote(this.emoteQueue)
                }
            }
            
            // Clear existing reset timer
            if (this.emoteQueueTimer) {
                clearTimeout(this.emoteQueueTimer)
            }
            
            // Set timer to CLEAR the queue after inactivity
            // This starts a "new chain" if you wait too long
            this.emoteQueueTimer = setTimeout(() => {
                this.emoteQueue = []
                this.emoteKeyPressed = false
                this.emoteQueueTimer = null
            }, this.resetQueueDelay)
            
        } else {
            // Non-emote key pressed, exit emote mode
            this.emoteKeyPressed = false
            this.emoteQueue = []
            if (this.emoteQueueTimer) {
                clearTimeout(this.emoteQueueTimer)
                this.emoteQueueTimer = null
            }
            
            this.processKey(key)
        }
    }
    
    // Removed legacy sendEmoteQueue as we send instantly now

    processKey(key) {
        if (key in this.keys) {
            let k = this.keys[key]

            this.keyActions[k.action](k.value || null)
        }
    }

    sendMove(x, y, frame = null) {
        if (!this.visible) {
            return
        }

        this.penguin.move(x, y, frame)
    }

    sendFrame(frame, set = true) {
        if (!this.visible || this.isTweening) {
             return
        }

        // Track penguin actions
        if (window.mixpanel) {
            window.mixpanel.trackPlayerAction('penguin_frame', { frame, set })
        }

        this.lockRotation = true

        this.penguin.playFrame(frame, set)
        this.network.send('send_frame', { set: set, frame: frame })
    }

    sendSit(pointer) {
        if (!this.visible || this.isTweening) {
            return
        }

        this.lockRotation = true

        this.penguin.sit(pointer.x, pointer.y)
    }

    sendSnowball(x, y) {
        if (!this.visible || this.isTweening) {
            return
        }

        // Track snowball throw
        if (window.mixpanel) {
            window.mixpanel.trackPlayerAction('snowball_throw', { x, y })
        }

        this.lockRotation = true

        this.interface.main.snowballFactory.throwBall(this.id, x, y)
        this.network.send('snowball', { x: x, y: y })
    }

    sendEmote(emote) {
        if (!this.visible) {
            return
        }

        // Track emote usage
        if (window.mixpanel) {
            window.mixpanel.trackEmote(emote)
        }

        this.interface.showEmoteBalloon(this.id, emote)
        this.network.send('send_emote', { emote: emote })
    }

    /* removed sendEmoteRapid as we use direct processEmote now */

    sendMultiEmote(emotes) {
        if (!this.visible || !Array.isArray(emotes) || emotes.length === 0) {
            return
        }

        console.log('[MultiEmote] Sending multi-emote:', emotes)

        // Track multi-emote usage
        if (window.mixpanel) {
            window.mixpanel.trackEmote(emotes[0]) // Track first emote
            window.mixpanel.trackPlayerAction('multi_emote', { count: emotes.length, emotes })
        }

        this.interface.showEmoteBalloon(this.id, emotes)
        this.network.send('send_emote', { emotes: emotes })
    }

    sendSafe(safe) {
        if (!this.visible) {
            return
        }

        let message = this.interface.main.safe.safeMessagesMap[safe]

        this.interface.showTextBalloon(this.id, message)
        this.network.send('send_safe', { safe: safe })
    }

    sendJoke() {
        const randomJokeId = Phaser.Math.Between(0, this.crumbs.jokes.length - 1)

        this.interface.showTextBalloon(this.id, this.crumbs.jokes[randomJokeId], false)
        this.network.send('send_joke', { joke: randomJokeId })
    }

    sendTour() {
        if (this.penguin.equipped.head.id !== 428) {
            this.interface.prompt.showError('Sorry, you must wear the tour guide\nhat to use this feature')
            return
        }

        const roomName = this.world.room.key.toLowerCase()

        if (roomName in this.crumbs.tour_messages) {
            const roomId = this.world.room.id

            this.interface.showTourMessage(this.id, roomId)
            this.network.send('send_tour', { roomId })
        }
    }

    showCrosshair() {
        if (!this.visible || !this.interface.main) {
            return
        }

        this.interface.main.onSnowballClick()
    }

    sendJoinRoom(id, name, x = 0, y = 0, randomRange = 40) {
        if (this.activeSeat) {
            return this.interface.prompt.showError('Please exit your game before leaving the room')
        }

        this.interface.showLoading(this.getString('joining', name))

        this.lockRotation = false

        this.lastJoinPos = { x, y }

        let random = PathEngine.getRandomPos(x, y, randomRange)
        this.network.send('join_room', { room: id, x: random.x, y: random.y })
    }

    sendJoinLastRoom() {
        if (!this.world.lastRoom || this.world.room && this.world.lastRoom === this.world.room.id) {
            return
        }

        const room = this.crumbs.rooms[this.world.lastRoom]

        if (room) {
            this.sendJoinRoom(this.world.lastRoom, room.key, room.x, room.y, 80)
        }
    }

    sendJoinIgloo(id) {
        console.log('[ClientController] sendJoinIgloo called with id:', id)
        
        if (this.world.room.isIgloo && this.world.room.id == id) {
            console.log('[ClientController] Already in this igloo, skipping')
            return
        }

        if (this.activeSeat) {
            console.log('[ClientController] Player in game, showing error')
            return this.interface.prompt.showError('Please exit your game before leaving the room')
        }

        console.log('[ClientController] Showing loading and sending join_igloo packet')
        this.interface.showLoading(this.getString('joining', 'igloo'))

        this.lockRotation = false

        this.network.send('join_igloo', { igloo: id, x: 0, y: 0 })
    }

    sendJoinGame(id) {
        if (this.activeSeat) {
            return this.interface.prompt.showError('Please exit your game before leaving the room')
        }

        this.network.send('join_room', { room: id, x: 0, y: 0 })
    }

    sendJoinTable(id) {
        this.network.send('join_table', { table: id })
    }

    sendMoveToSeat(id, seat, type = 'table') {
        let container

        switch (type) {
            case 'table':
                container = this.world.room.getTable(id)
                break
            case 'waddle':
                container = this.world.room.getWaddle(id)
                break
        }

        if (!container) {
            return
        }

        seat = container[`seat${seat}`]

        if (seat) {
            this.activeSeat = seat

            let pos = this.getSeatWorldPos(seat)
            this.sendMove(pos.x, pos.y, seat.sitFrame)
        } else {
            this.activeSeat = true
        }
    }

    sendLeaveSeat() {
        if (!this.activeSeat) {
            return
        }

        let done = this.activeSeat.donePoint

        if (done) {
            let pos = this.getSeatWorldPos(done)
            this.sendMove(pos.x, pos.y)
        }

        this.activeSeat = null
        this.world.events.emit('leftseat')
    }

    getSeatWorldPos(seat) {
        let matrix = seat.getWorldTransformMatrix()

        return {
            x: matrix.getX(0, 0),
            y: matrix.getY(0, 0)
        }
    }

    async stampEarned(stamp) {
        const stampId = parseInt(stamp)

        if (!Number.isFinite(stampId)) {
            return
        }

        if (!this.stamps.includes(stampId)) {
            // Use WebAPI for stamp collection (CPI architecture pattern)
            if (this.network.webapi?.isAuthenticated()) {
                try {
                    const result = await this.network.webapi.earnStamp(stampId)
                    if (result && !result.error) {
                        // Update local state
                        this.stamps.push(stampId)
                        return
                    }
                } catch (error) {
                    console.warn('WebAPI stamp collection failed, falling back to socket:', error)
                }
            }
            
            // Fallback to Socket.IO
            this.network.send('collect_stamp', { stamp: stampId })
        }
    }

    addPostcard(postcard) {
        if (!this.postcards) {
            this.postcards = []
        }
        this.postcards.push(postcard)

        this.refreshPostcards()
    }

    /**
     * Sort by newest first.
     */
    sortPostcards() {
        if (this.postcards && Array.isArray(this.postcards)) {
            this.postcards.sort((a, b) => new Date(b.sendDate) - new Date(a.sendDate))
        }
    }

    filterPostcards(filter) {
        if (!this.postcards) {
            this.postcards = []
        }
        this.postcards = this.postcards.filter(postcard => filter(postcard))

        this.refreshPostcards()
    }

    refreshPostcards() {
        this.sortPostcards()

        if (this.interface.main.mail?.visible) {
            // Read mail before updating count
            this.interface.main.mail.goToFirstPage()
        }

        this.interface.main.updateMailCount()
    }

    startWalkingPet(petId) {
        this.network.send('pet_start_walk', { id: petId })
    }

}
